import { useRef } from 'react'
import { createPortal } from 'react-dom'

import {
  type CalendarConstraint,
  DateHelper,
  DatePicker,
  DragDropFile,
  DragDropFileProvider,
  ErrorMessage,
  type FileWithStatus,
} from '@loadsmart/loadsmart-ui'
import { Layout, Field, Icon, Dialog } from '@loadsmart/miranda-react'
import type { Dialog as MirandaWCDialog } from '@loadsmart/miranda-wc'

import {
  bytesToMegabytes,
  MAX_FILE_SIZE_IN_BYTES,
} from '../../../presenters/shipperDocumentUploadPresenter'
import { StyledSelect, DatePickerWrapper } from './UploadDocumentDialog.styles'
import { useUploadDocumentDialog } from './useUploadDocumentDialog'

export type UploadDocumentDialogProps = {
  open: boolean
  shipperUUID: string
  onClose: () => void
  onUploadSuccess?: () => void
}

export function UploadDocumentDialog({
  open,
  shipperUUID,
  onClose,
  onUploadSuccess,
}: UploadDocumentDialogProps) {
  const {
    document: shipperDocument,
    errorMessage,
    typeOptions,
    currentTypeOption,
    isCOI,
    allowedFileExtensions,
    canUpload,
    handleUploadClick,
    handleDocumentTypeChange,
    handleDateChange,
    handleFileRemoved,
    handleFilesAdded,
  } = useUploadDocumentDialog({ shipperUUID, onClose, onUploadSuccess })

  const dialogRef = useRef<MirandaWCDialog>(null)

  const today = DateHelper().toString()

  // Do not allow selecting today nor days in the past:
  const datePickerConstraints: CalendarConstraint[] = [[null, today]]

  const maxSizeInMB = bytesToMegabytes(MAX_FILE_SIZE_IN_BYTES)
  const hintMessage = `Upload a single file. Maximum file size: ${maxSizeInMB} MB.`

  const uploadDisabled = !canUpload()

  const fileList: FileWithStatus[] = []
  if (shipperDocument.file) {
    fileList.push({
      file: shipperDocument.file,
      status: 'success',
    })
  }

  // This "handleOpened" function prevents the select and date-picker from getting clipped by the dialog borders.
  // The inner <dialog> lives in m-dialog's shadow DOM, so we can't solve it via CSS.
  // Its default "overflow: auto" clips loadsmart-ui dropdowns.
  function handleOpened() {
    const dialog = dialogRef.current
    if (!dialog) return
    const nativeDialog = dialog.shadowRoot?.querySelector('dialog')
    if (nativeDialog) {
      nativeDialog.style.overflow = 'visible'
    }
  }

  return createPortal(
    <Dialog
      ref={dialogRef}
      open={open}
      size="medium"
      onOpened={handleOpened}
      data-testid="dialog-upload-document"
    >
      <Dialog.Header>
        Upload document
        <Dialog.Close onClick={onClose} />
      </Dialog.Header>

      <Dialog.Body>
        <Layout.Stack>
          <Field required>
            <Field.Label>Document type</Field.Label>
            <StyledSelect
              id="document-type-select"
              name="document-type-select"
              data-testid="document-type-select"
              placeholder="Select type of document"
              options={typeOptions}
              onChange={handleDocumentTypeChange}
              value={currentTypeOption}
              hideClear
            />
          </Field>
          {isCOI && (
            <Field required data-testid="expire-date-field">
              <Field.Label>Expire date</Field.Label>
              <DatePickerWrapper>
                <DatePicker
                  id="expire-date"
                  name="expire-date"
                  value={shipperDocument.expirationDate}
                  constraints={datePickerConstraints}
                  onChange={handleDateChange}
                />
              </DatePickerWrapper>
            </Field>
          )}
          <DragDropFileProvider
            fileList={fileList}
            onFileRemoved={handleFileRemoved}
            onFilesAdded={handleFilesAdded}
          >
            <DragDropFile.Wrapper>
              <DragDropFile.DropZone
                hint={hintMessage}
                multiple={false}
                accept={allowedFileExtensions}
                error={Boolean(errorMessage)}
              />
              <ErrorMessage>{errorMessage}</ErrorMessage>
              <DragDropFile.FileList />
            </DragDropFile.Wrapper>
          </DragDropFileProvider>
        </Layout.Stack>
      </Dialog.Body>

      <Dialog.Actions>
        <Dialog.ActionTertiary onClick={onClose}>Cancel</Dialog.ActionTertiary>
        <Dialog.ActionPrimary
          data-testid="upload-button"
          disabled={uploadDisabled}
          aria-disabled={uploadDisabled}
          onClick={handleUploadClick}
        >
          <Icon name="upload" color="color-neutral-0" />
          Upload
        </Dialog.ActionPrimary>
      </Dialog.Actions>
    </Dialog>,
    document.body
  )
}
